<template>
  <div class="mt-4">
    <h4>Set Allowed Sellers</h4>

    <CForm
      novalidate
      @submit.prevent
    >
      <CRow>
        <CCol
          :md="{ size: '3' }"
          :sm="{ size: '3' }"
        >
          <div class="form-group">
            <label>Select Category</label>

            <div>
              <v-select
                v-model="selectedCategory"
                label="name"
                :options="categories"
                placeholder="Search categories"
                @input="fetchSellerOffersForCategory(selectedCategory.id)"
              />
            </div>
          </div>
        </CCol>

        <CCol
          v-if="selectedCategory"
          :md="{ size: '9' }"
          :sm="{ size: '9' }"
        >
          <h4>
            Select Allowed Sellers for {{ selectedCategory.name }}
          </h4>
          
          <CForm>
            <div
              v-if="isLoadingSellerOffers"
              class="text-center my-3"
            >
              <CSpinner />
              <div>Loading sellers...</div>
            </div>
            <div
              v-else-if="sellersWithApprovedRegistrations.length === 0"
              class="my-3"
            >
              No sellers in this category
            </div>
            <div
              v-else
              class="my-3"
            >
              <v-multiselect-listbox
                v-model="allowedSellerIds"
                :options="sellerOptions"
                :reduce-display-property="option => option.name"
                :reduce-value-property="option => option.id"
                class="msl-multi-select--full-width"
                search-input-class="form-control"
                search-options-placeholder="Search Sellers"
                selected-options-placeholder="Search Allowed Sellers"
                selected-no-options-text="All sellers allowed"
              />
            </div>
          </CForm>

          <div class="form-row">
            <ButtonLoading
              type="submit"
              size="sm"
              color="primary"
              :loading="isLoading"
              :disabled="isLoadingAllSellersAllowed"
              @click="onSaveAllowedSellers"
            >
              Save
            </ButtonLoading>

            <ButtonLoading
              type="submit"
              size="sm"
              color="success"
              :loading="isLoadingAllSellersAllowed"
              :disabled="isLoading"
              class="ml-2"
              @click="allowAllSellers"
            >
              Allow All Sellers
            </ButtonLoading>
          </div>
        </CCol>
      </CRow>
    </CForm>
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { showSuccessMessage, showErrorMessage } from '@/notification-utils'

/**
 * @typedef {Object} SellerOfferGetters
 * @property {Array<import('@/store/seller-account').SellerAccount>} sellersWithApprovedRegistrations
 */

export default {
  name: 'BuyerTrafficControl',
  
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      selectedCategory: null,
      isLoading: false,
      isLoadingSellerOffers: false,
      allowedSellerIds: [],
      isLoadingAllSellersAllowed: false,
    }
  },

  computed: {
    ...mapState('category', ['categories']),

    ...mapState('buyerTrafficControl', [
      'buyerAllowedSellers',
    ]),

    /**
     * Seller Offer Getters
     * @type {SellerOfferGetters}
     */
    ...mapGetters('sellerOffer', ['sellersWithApprovedRegistrations']),

    allowedSellersForSelectedCategory() {
      return this.buyerAllowedSellers[`${this.accountId}-${this.selectedCategory?.id}`] || []
    },
    
    sellerOptions() {
      return this.sellersWithApprovedRegistrations
    },
  },

  watch: {
    allowedSellersForSelectedCategory(newValue) {
      this.allowedSellerIds = newValue.map(seller => seller.id)
    }
  },

  async created() {
    if (this.categories.length <= 1) {
      await this.getAllCategories()
    }
  },

  methods: {
    ...mapActions('category', {
      getAllCategories: 'list'
    }),

    ...mapActions('sellerOffer', {
      getSellerOffers: 'list'
    }),

    ...mapActions('buyerTrafficControl', [
      'fetchAllowedSellers',
      'updateAllowedSellers'
    ]),

    async fetchSellerOffersForCategory(categoryId) {
      this.isLoadingSellerOffers = true

      try {
        await this.getSellerOffers({
          categoryId,
          includeRegistrations: true
        })

        await this.fetchAllowedSellers({
          categoryId,
          buyerId: this.accountId
        })
      } catch (error) {
        // Handle error if needed
      } finally {
        this.isLoadingSellerOffers = false
      }
    },

    async onSaveAllowedSellers() {
      this.isLoading = true

      try {
        await this.updateAllowedSellers({
          buyerId: this.accountId,
          categoryId: this.selectedCategory.id,
          sellerIds: this.allowedSellerIds
        })
        
        showSuccessMessage('Allowed sellers for this saved successfully')
      } catch (error) {
        showErrorMessage('Failed to save allowed sellers')
      } finally {
        this.isLoading = false
      }
    },

    async allowAllSellers() {
      this.isLoadingAllSellersAllowed = true
      this.allowedSellerIds = []

      try {
        await this.updateAllowedSellers({
          buyerId: this.accountId,
          categoryId: this.selectedCategory.id,
          sellerIds: this.allowedSellerIds
        })
        
        showSuccessMessage('All sellers have been allowed for this buyer')
      } catch (error) {
        showErrorMessage('Failed to allow all sellers')
      } finally {
        this.isLoadingAllSellersAllowed = false
      }
    }
  }
}
</script>
<style scoped>
  
</style>